import React from 'react';
import Downshift from 'downshift';

import css from './DropDownList.module.scss';

interface Props {
  placeholder?: string;
  options: DropDownListOption[];
  initialSelection?: string;
  onChange: (value: string, label: string) => void;
  error?: boolean;
}

export interface DropDownListOption {
  value: string;
  label: string;
  'data-testid'?: string;
  'data-e2e'?: string;
}

const DropDownList = ({
  options,
  placeholder,
  initialSelection,
  onChange,
  error
}: Props) => {
  function handleChange(selectedValue: string, selectedLabel: string) {
    onChange(selectedValue, selectedLabel);
  }

  function getLabelFromValue(value: any) {
    return options.find(option => value.label === option.label)?.label || '';
  }

  const genderToPronouns = {
    male: 'he/him',
    female: 'she/her',
    other: 'they/them'
  };

  return (
    <Downshift
      onChange={selected => handleChange(selected.value, selected.label)}
      itemToString={item => (item ? item.value : '')}
      initialInputValue={initialSelection}
      data-testid="dropdown"
    >
      {({
        isOpen,
        getToggleButtonProps,
        getRootProps,
        getItemProps,
        getMenuProps,
        selectedItem: dsSelectedItem,
        inputValue,
        getLabelProps
      }) => (
        <div
          className={css.DropDownRoot}
          {...getRootProps(
            {
              role: undefined /* Remove the combo-box role - have to squash types */
            } as any,
            { suppressRefError: true }
          )}
        >
          <label {...getLabelProps()} style={{ display: 'none' }}>
            {placeholder}
          </label>
          <button
            data-testid="dropdown-button"
            data-e2e="dropdown-button"
            className={!error ? css.DropDownControl : css.DropDownError}
            {...getToggleButtonProps()}
          >
            {inputValue !== ''
              ? getLabelFromValue(dsSelectedItem || '') ||
                genderToPronouns[inputValue || '']
              : placeholder}
          </button>
          <span className={css.DropDownArrow} />
          <div style={{ position: 'relative' }}>
            {/* if the input element is open, render the div else render nothing */}
            {isOpen ? (
              <div className={css.DropDownMenu} {...getMenuProps()}>
                {options.map((item, index) => (
                  <div
                    data-testid={item['data-testid']}
                    data-e2e={item['data-e2e']}
                    className={css.DropDownOption}
                    key={`option-${index}`}
                    {...getItemProps({ key: index, index, item })}
                  >
                    <input
                      readOnly
                      id={item.value}
                      className={`${css.radioButtonInput}`}
                      type="radio"
                      value={item.value}
                      checked={
                        dsSelectedItem &&
                        item &&
                        dsSelectedItem.label === item.label
                      }
                    />
                    <span className={css.Checkmark}></span>
                    <label htmlFor={item.value}>{item.label}</label>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </Downshift>
  );
};

export default DropDownList;
